import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import {
  Building2, Home, HardHat, Sparkles, Building, Briefcase,
  Package, KeyRound, Trash2, Truck, Wind, Sofa, ArrowRight,
} from 'lucide-react';
import { getServices } from '../api/api';

// Doit rester aligné avec iconOptions dans AdminServices : une icône proposée
// dans le panel mais absente ici renvoie undefined et fait planter le rendu.
const iconMap = {
  Building2, Home, HardHat, Sparkles, Building, Briefcase,
  Package, KeyRound, Trash2, Truck, Wind, Sofa,
};

export const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (loading) {
    return (
      <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">Chargement des services...</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
            Nos services de nettoyage à Liège
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des solutions professionnelles adaptées à tous vos besoins de nettoyage
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            // Repli : une icône inconnue ne doit jamais casser la page publique.
            const IconComponent = iconMap[service.icon] || Sparkles;
            return (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                className="group block h-full"
                style={{ animationDelay: `${index * 100}ms` }}
                aria-label={`En savoir plus : ${service.title}`}
              >
              <Card
                className="h-full hover:shadow-lg transition-all duration-500 border-0 bg-white overflow-hidden hover:-translate-y-0.5"
              >
                {/* Service Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="400"
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent group-hover:from-blue-900/90 transition-all duration-500"></div>
                  
                  {/* Icon Overlay */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-xl group-hover:bg-blue-600 transition-colors duration-300">
                    <IconComponent className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Lien vers la page détail du service */}
                  <div className="mt-4 inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm">
                    En savoir plus
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </div>
                </CardContent>
              </Card>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-6">
            Besoin d'un service personnalisé ?
          </p>
          <Link
            to="/devis"
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-600/30 transition-all duration-300"
          >
            Demander un devis personnalisé
          </Link>
        </div>
      </div>
    </section>
  );
};
